/**
 * Rosa della squadra-stagione (V07), con il tesseramento (V08) in un dialog
 * e il collegamento all'import (V09).
 *
 * I tesserati stanno in quattro card, una per reparto, una sotto l'altra;
 * dentro la card i giocatori vanno su due colonne sugli schermi larghi. Ogni
 * giocatore e una riga come nelle altre liste: la maglia con il numero, con
 * la fascia del colore dello stato di salute e la sigla del ruolo, poi nome, anno, piede, stati, minuti e voto medio, e a
 * destra l'overall. Il voto medio arriva solo a chi legge i voti, overall e
 * medie per famiglia di attributi (fisici, tecnici, mentali; per i portieri
 * la famiglia portiere al posto dei tecnici) solo a chi legge le misurazioni.
 * Una tendina ordina tutte le card insieme, per cognome in partenza. L'Admin
 * aggiunge giocatori, cambia numero e date, chiude il tesseramento. La riga
 * apre la scheda (V36): per Mister e Dirigente solo i tesseramenti in corso,
 * perche gli altri potrebbero non essere piu nelle loro rose della stagione
 * corrente.
 *
 * Chi ha chiuso il tesseramento resta in fondo, raccolto: la rosa di una
 * stagione passata deve restare leggibile.
 */

import { type FormEvent, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { CheckIcon, ChevronsUpDownIcon, DownloadIcon, OctagonXIcon, PlusIcon } from 'lucide-react';
import { nuovoTesseramentoSchema, puo } from '@calcio/shared';
import { toast } from 'sonner';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from '@/components/ui/empty';
import { Field, FieldDescription, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { AzioniTestata, MenuAzioni, PulsanteInvio, PulsanteNuovo } from '../comuni/Azioni.js';
import { chiama } from '../api/client.js';
import { StatoCaricamento, Testata } from '../comuni/Testata.js';
import { Corpo } from '../comuni/Corpo.js';
import { Filtro } from '../comuni/FiltriVista.js';
import { erroriPerCampo, messaggio, useDati } from '../comuni/dati.js';
import { useContesto } from '../navigazione/contesto.js';
import { type Ordine, ordina } from '../statistiche/statistiche.js';
import { FormTesseramento } from './FormTesseramento.js';
import {
  type Giocatore,
  type MedieAttributi,
  type RigaRosa,
  REPARTI,
  annoDi,
  conMedie,
  dividiRosa,
  oggi,
  perReparto,
} from './elenchi.js';
import { RigaGiocatore } from './RigaGiocatore.js';
import { StatoVuoto } from '../comuni/StatoVuoto.js';

type Colonna = 'numeroMaglia' | 'minuti' | 'votoMedio' | keyof MedieAttributi;

/** Le voci della tendina: il numero parte dal piu basso, come sulla distinta, i valori dal piu alto. */
const ORDINI: Record<string, { etichetta: string; ordine: Ordine<Colonna> }> = {
  cognome: { etichetta: 'Cognome', ordine: { colonna: 'giocatore', verso: 'asc' } },
  numero: { etichetta: 'Numero di maglia', ordine: { colonna: 'numeroMaglia', verso: 'asc' } },
  overall: { etichetta: 'Overall', ordine: { colonna: 'overall', verso: 'desc' } },
  minuti: { etichetta: 'Minuti', ordine: { colonna: 'minuti', verso: 'desc' } },
  voto: { etichetta: 'Voto medio', ordine: { colonna: 'votoMedio', verso: 'desc' } },
};

export function Rosa() {
  const { ss } = useParams<{ ss: string }>();
  const { utente, ricaricaSquadre } = useContesto();
  const { dati: righe, errore, ricarica } = useDati<RigaRosa[]>(`/squadre-stagione/${ss}/rosa`);
  const [ordinePer, setOrdinePer] = useState('cognome');
  const [aggiunta, setAggiunta] = useState(false);
  const [inModifica, setInModifica] = useState<RigaRosa | null>(null);

  const gestisce = puo(utente.assegnazioni, 'tesseramento.gestisci', ss);

  if (!righe) {
    return (
      <Corpo>
        <StatoCaricamento errore={errore} />
      </Corpo>
    );
  }

  const { inRosa, usciti } = dividiRosa(righe, oggi());
  const conVoti = righe.some((r) => 'votoMedio' in r);
  const conMedieAttributi = righe.some((r) => 'medieAttributi' in r);
  const ordine = (ORDINI[ordinePer] ?? ORDINI.cognome!).ordine;

  const aggiorna = async () => {
    // Il numero di tesserati compare anche nel selettore e nella scelta della squadra.
    await Promise.all([ricarica(), ricaricaSquadre()]);
  };

  const azioni = gestisce && (
    <AzioniTestata>
      <PulsanteNuovo etichetta="Aggiungi giocatore" onClick={() => setAggiunta(true)} />
      <MenuAzioni
        voci={[{ etichetta: 'Importa rosa', Icona: DownloadIcon, a: `/sq/${ss}/rosa/import` }]}
      />
    </AzioniTestata>
  );
  const aggiungi = gestisce && (
    <div className="flex flex-wrap gap-2">
      <Button asChild variant="outline">
        <Link to={`/sq/${ss}/rosa/import`}>
          <DownloadIcon data-icon="inline-start" />
          Importa rosa
        </Link>
      </Button>
      <Button onClick={() => setAggiunta(true)}>
        <PlusIcon data-icon="inline-start" />
        Aggiungi giocatore
      </Button>
    </div>
  );

  const elenco = (gruppo: RigaRosa[], chiusi: boolean) => (
    <ul className="grid gap-2 @5xl:grid-cols-2">
      {ordina(gruppo.map(conMedie), ordine).map((r) => (
        <li key={r.tesseramentoId}>
          <RigaGiocatore
            riga={r}
            chiusi={chiusi}
            // Mister e Dirigente non aprono chi e uscito: potrebbe non essere piu loro.
            apribile={gestisce || !chiusi}
            conVoti={conVoti}
            conMedie={conMedieAttributi}
            onModifica={gestisce ? () => setInModifica(r) : undefined}
          />
        </li>
      ))}
    </ul>
  );

  return (
    <div>
      <Testata titolo="Rosa" voce="rosa" azione={righe.length > 0 && azioni} />
      <Corpo>
        {righe.length === 0 ? (
          <Empty>
            <EmptyHeader>
              <EmptyTitle>Rosa vuota</EmptyTitle>
              <EmptyDescription>
                {gestisce
                  ? "Importa la rosa di un'altra squadra o stagione, oppure aggiungi i giocatori uno per uno."
                  : "I tesseramenti li gestisce l'Admin Squadra della società."}
              </EmptyDescription>
            </EmptyHeader>
            {aggiungi && <EmptyContent>{aggiungi}</EmptyContent>}
          </Empty>
        ) : (
          <div className="flex flex-col gap-4">
            <div className="flex flex-wrap items-end gap-3">
              <Filtro
                etichetta="Ordina per"
                valore={ordinePer}
                onCambia={setOrdinePer}
                opzioni={Object.entries(ORDINI)
                  .filter(
                    ([chiave]) =>
                      (chiave !== 'overall' || conMedieAttributi) && (chiave !== 'voto' || conVoti),
                  )
                  .map(([valore, { etichetta }]) => ({ valore, etichetta }))}
              />
            </div>

            {inRosa.length > 0 ? (
              <div className="flex flex-col gap-4">
                {perReparto(inRosa).map((gruppo, i) => (
                  <Card key={REPARTI[i]} className="bg-muted">
                    <CardHeader>
                      <CardTitle className="flex items-center justify-center gap-2 font-semibold tracking-wide uppercase">
                        <h2>{REPARTI[i]}</h2>
                        <Badge className="tabular-nums">{gruppo.length}</Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="px-2 @xl:px-(--card-spacing)">
                      {gruppo.length > 0 ? (
                        elenco(gruppo, false)
                      ) : (
                        <StatoVuoto titolo="Nessuno in rosa" />
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <StatoVuoto titolo="Nessun tesseramento in corso" />
            )}

            {usciti.length > 0 && (
              <details>
                <summary className="flex min-h-12 cursor-pointer items-center font-semibold text-muted-foreground">
                  Usciti dalla rosa · {usciti.length}
                </summary>
                {elenco(usciti, true)}
              </details>
            )}
          </div>
        )}

        {gestisce && (
          <>
            <Dialog open={aggiunta} onOpenChange={setAggiunta}>
              <DialogContent>
                {aggiunta && (
                  <FormAggiunta
                    ss={ss!}
                    giaInRosa={new Set(inRosa.filter((r) => !r.dataFine).map((r) => r.giocatoreId))}
                    onChiudi={() => setAggiunta(false)}
                    onAggiunto={async () => {
                      setAggiunta(false);
                      await aggiorna();
                    }}
                  />
                )}
              </DialogContent>
            </Dialog>

            <Dialog open={inModifica !== null} onOpenChange={(a) => !a && setInModifica(null)}>
              <DialogContent>
                {inModifica && (
                  <FormTesseramento
                    key={inModifica.tesseramentoId}
                    ss={ss!}
                    tesseramentoId={inModifica.tesseramentoId}
                    nome={inModifica.nome}
                    cognome={inModifica.cognome}
                    numeroMaglia={inModifica.numeroMaglia}
                    dataInizio={inModifica.dataInizio}
                    dataFine={inModifica.dataFine}
                    onChiudi={() => setInModifica(null)}
                    onSalvato={async () => {
                      setInModifica(null);
                      await aggiorna();
                    }}
                  />
                )}
              </DialogContent>
            </Dialog>
          </>
        )}
      </Corpo>
    </div>
  );
}

/**
 * Aggiunta di un giocatore gia in anagrafica. La ricerca sta in un elenco
 * filtrabile perche le anagrafiche possono essere migliaia; chi non c'e si
 * crea dal nuovo giocatore, che torna qui gia tesserato.
 */
function FormAggiunta({
  ss,
  giaInRosa,
  onChiudi,
  onAggiunto,
}: {
  ss: string;
  giaInRosa: Set<string>;
  onChiudi: () => void;
  onAggiunto: () => Promise<void>;
}) {
  const { dati: giocatori, errore } = useDati<Giocatore[]>('/giocatori');
  const [scelto, setScelto] = useState<Giocatore | null>(null);
  const [apertoElenco, setApertoElenco] = useState(false);
  const [numero, setNumero] = useState('');
  const [errori, setErrori] = useState<Record<string, string>>({});
  const [erroreServer, setErroreServer] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);

  const candidati = (giocatori ?? []).filter((g) => !giaInRosa.has(g.id));

  const invia = async (e: FormEvent) => {
    e.preventDefault();
    const esito = nuovoTesseramentoSchema.safeParse({
      giocatoreId: scelto?.id,
      numeroMaglia: numero === '' ? null : Number(numero),
    });
    if (!esito.success) {
      const perCampo = erroriPerCampo(esito.error);
      if (perCampo.giocatoreId) perCampo.giocatoreId = 'Scegli un giocatore.';
      return setErrori(perCampo);
    }
    setErrori({});
    setErroreServer(null);
    setInCorso(true);
    try {
      await chiama(`/squadre-stagione/${ss}/tesseramenti`, { metodo: 'POST', corpo: esito.data });
      await onAggiunto();
      toast.success('Giocatore aggiunto alla rosa.');
    } catch (errore) {
      setErroreServer(messaggio(errore));
      setInCorso(false);
    }
  };

  return (
    <form onSubmit={(e) => void invia(e)} className="flex flex-col gap-6">
      <DialogHeader>
        <DialogTitle>Aggiungi giocatore</DialogTitle>
        <DialogDescription>
          Il tesseramento parte da oggi. Non trovi chi cerchi?{' '}
          <Link to={`/giocatori/nuovo?ss=${ss}`} className="underline underline-offset-4">
            Crea un nuovo giocatore
          </Link>
          .
        </DialogDescription>
      </DialogHeader>

      <FieldGroup>
        <Field data-invalid={Boolean(errori.giocatoreId)}>
          <FieldLabel htmlFor="aggiunta-giocatore">Giocatore</FieldLabel>
          <Popover open={apertoElenco} onOpenChange={setApertoElenco}>
            <PopoverTrigger asChild>
              <Button
                id="aggiunta-giocatore"
                type="button"
                variant="outline"
                role="combobox"
                aria-expanded={apertoElenco}
                aria-invalid={Boolean(errori.giocatoreId)}
                className="w-full justify-between font-normal"
              >
                {scelto ? `${scelto.cognome} ${scelto.nome}` : 'Cerca per nome o cognome'}
                <ChevronsUpDownIcon data-icon="inline-end" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-(--radix-popover-trigger-width) p-0" align="start">
              <Command>
                <CommandInput placeholder="Cerca…" />
                <CommandList>
                  <CommandEmpty>
                    {giocatori ? 'Nessun giocatore trovato.' : (errore ?? 'Carico…')}
                  </CommandEmpty>
                  <CommandGroup>
                    {candidati.map((g) => (
                      <CommandItem
                        key={g.id}
                        value={`${g.cognome} ${g.nome} ${g.id}`}
                        onSelect={() => {
                          setScelto(g);
                          setApertoElenco(false);
                        }}
                      >
                        <span className="flex-1">
                          {g.cognome} {g.nome}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {annoDi(g.dataNascita)}
                        </span>
                        {scelto?.id === g.id && <CheckIcon />}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          <FieldError>{errori.giocatoreId}</FieldError>
        </Field>

        <Field data-invalid={Boolean(errori.numeroMaglia)}>
          <FieldLabel htmlFor="aggiunta-numero">Numero di maglia</FieldLabel>
          <Input
            id="aggiunta-numero"
            type="number"
            inputMode="numeric"
            min={1}
            max={99}
            value={numero}
            aria-invalid={Boolean(errori.numeroMaglia)}
            onChange={(e) => setNumero(e.target.value)}
          />
          <FieldDescription>Facoltativo.</FieldDescription>
          <FieldError>{errori.numeroMaglia}</FieldError>
        </Field>
      </FieldGroup>

      {erroreServer && (
        <Alert variant="destructive">
          <OctagonXIcon />
          <AlertTitle>Salvataggio non riuscito</AlertTitle>
          <AlertDescription>{erroreServer}</AlertDescription>
        </Alert>
      )}

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onChiudi}>
          Annulla
        </Button>
        <PulsanteInvio type="submit" inCorso={inCorso}>
          Aggiungi alla rosa
        </PulsanteInvio>
      </DialogFooter>
    </form>
  );
}
